﻿import { Toast as BsToast, ToastContainer } from 'react-bootstrap';
import { useState, createContext, useContext, useCallback, type ReactNode } from 'react';

interface ToastMessage {
  id: number;
  title: string;
  message: string;
  variant: 'success' | 'danger' | 'warning' | 'info';
}

interface ToastContextType {
  showToast: (title: string, message: string, variant?: ToastMessage['variant']) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((title: string, message: string, variant: ToastMessage['variant'] = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, title, message, variant }]);
  }, []);

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Register global toast for use outside React
  setGlobalToast(showToast);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer position="top-end" className="p-3 toast-container-fixed">
        {toasts.map(toast => (
          <BsToast key={toast.id} bg={toast.variant} onClose={() => removeToast(toast.id)} delay={4000} autohide>
            <BsToast.Header><strong className="me-auto">{toast.title}</strong></BsToast.Header>
            <BsToast.Body className={toast.variant === 'danger' || toast.variant === 'success' ? 'text-white' : ''}>
              {toast.message}
            </BsToast.Body>
          </BsToast>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}


// Global toast function for use outside React components (e.g., API client)
let globalShowToast: ((title: string, message: string, variant?: 'success' | 'danger' | 'warning' | 'info') => void) | null = null;

export function setGlobalToast(fn: typeof globalShowToast) {
  globalShowToast = fn;
}

export function showGlobalToast(title: string, message: string, variant: 'success' | 'danger' | 'warning' | 'info' = 'info') {
  if (globalShowToast) {
    globalShowToast(title, message, variant);
  }
}

